import { ISubject } from '@/api/subject';
import { ContentTypeEnum } from '@/api/thread';
import { IDevice, IDeviceCategory } from '@/api/device';
import {
  MAX_VIDEO_SIZE_DISPLAY,
  MAX_IMAGE_SIZE_DISPLAY,
  IMAGE_ACCEPT_FORMAT,
  VIDEO_ACCEPT_FORMAT,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
  MAX_IMAGE_WIDTH,
  MAX_IMAGE_HEIGHT,
  MAX_VIDEO_DURATION,
  VIDEO_ACCEPT_FORMAT_DISPLAY,
  IMAGE_ACCEPT_FORMAT_DISPLAY,
  MAX_VIDEO_DURATION_DISPLAY,
} from '@/constants/upload';
import {
  AliOSSCheckPoint,
  AliOSSUploadConfig,
} from '@/api/upload';
import { parseMimeType } from '@/utils/mimeType';

export enum ActionTypeEnum {
  photo = 'photo',
  video = 'video',
  link = 'link',
}

export const customQueryTitleMap = {
  '1': '#Mimo咨询求助#',
};

export type UploadType = Omit<ActionTypeEnum, ActionTypeEnum.link>

export interface IRelatedContent {
  title: string,
  type_id: number,
  devices: IDevice[],
}

export interface IThreadValue extends IRelatedContent {
  subject: number;
  message: IEditorContentItem[],
}

export interface IProcessFile {
  url: string;
  file: File;
  error?: any;
  duration?: number;
  width?: number;
  height?: number;
}
export interface IEditorContentItem {
  key: string;
  type: ContentTypeEnum;
  file?: File;
  url?: string; // 图片 or 视频
  desc?: string; // 文字
  imgsrc?: string; // 图片 or 视频封面图
  percent?: number;
  duration?: number;
  isCompleted?: boolean; // 是否上传完
  error?: any;
  checkpoint?: AliOSSCheckPoint; //视频
  abort?: (cpt: AliOSSCheckPoint) => void; //视频
  nameindex?: string;
  controller?: AbortController; //图片
  ossConfig?: AliOSSUploadConfig;
}

export interface IEditorValue {
  items: IEditorContentItem[];
  title: string; // 标题
  type_id: number; // 主题分类
  devices: IDevice[];
}

export enum ModalType {
  LOCAL_DRAFT = 'localDraft',
  DELETE = 'delete',
  HAS_UPLOAD_FILE_ERROR= 'hasUploadFileError'
}

export interface IEditor {
  className?: string;
  subjects?: ISubject[]; // 主题分类
  recommendDevices?: IDevice[];
  deviceCategories?: IDeviceCategory[];
  value: IEditorValue;
  onChange: any,
  isEdit?: boolean;
  handleModal?: (type: ModalType) => void;
  canPost?: boolean;
}

export interface IOnChangeCbParam {
  acceptFiles: IProcessFile[];
  rejectFiles: IProcessFile[];
}

// 编辑按钮类型 图片，视频，链接
export interface IEditorAction {
  slug: ActionTypeEnum;
  label: string;
  isUploadFile: boolean;
  multiple?: boolean;
  accept?: string;
  callback: (e: React.MouseEvent<HTMLDivElement | HTMLInputElement, MouseEvent>, type?: UploadType) => void;
}

export const checkVideoDuration = (url: string) => (
  new Promise<any>((resolve) => {
    let mediaElem:any = null;
    mediaElem = new Audio(url);
    mediaElem.muted = true;
    mediaElem.play().then(() => mediaElem.pause());
    mediaElem.addEventListener('loadedmetadata', () => {
      window.URL.revokeObjectURL(mediaElem.src);
      if (mediaElem.duration <= MAX_VIDEO_DURATION) {
        resolve({
          checked: true,
          duration: mediaElem.duration,
          url,
        });
      } else {
        resolve({
          checked: false,
          duration: mediaElem.duration,
          error: 'MAX_VIDEO_DURATION',
        });
      }
    }, false);
    setTimeout(() => {
      resolve({
        checked: true,
        duration: mediaElem.duration ? mediaElem.duration : null,
        url,
      });
    }, 1000);
  })
);

export const getPhotoSize = (src: string) => (
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    image.onerror = () => resolve({
      width: 0,
      height: 0,
    });
    image.src = src;
  })
);

export const checkPhotoResolution = (url: string) => (
  getPhotoSize(url).then((size: any) => {
    const { width, height } = size;
    if (
      width <= MAX_IMAGE_WIDTH
      && height <= MAX_IMAGE_HEIGHT
    ) {
      return {
        checked: true,
        url,
        width,
        height,
      };
    }
    return {
      checked: false,
      error: 'MAX_IMAGE_RESOLUTION',
    };
  })
);

export const checkFileExtension = (file: File, accept: string) => {
  const extension = file && file.type ? file.type : '';
  if (accept && accept.split(', ').indexOf(extension.toLowerCase()) < 0) {
    return false;
  }
  return true;
};


export const getFileSize = (file: File, maxSize: number, accept: string) => (
  new Promise((resolve) => {
    if (file.size > maxSize) {
      resolve({
        checked: false,
        error: 'MAX_SIZE',
      });
    } else if (!checkFileExtension(file, accept)) {
      resolve({
        checked: false,
        error: 'IMAGE_ACCEPT_FORMAT',
      });
    } else {
      resolve({
        checked: true,
      });
    }
  })
);


export const getFileSizeLimitDisplay = (type: UploadType) => {
  if (type === ActionTypeEnum.video) {
    return MAX_VIDEO_SIZE_DISPLAY;
  }
  return MAX_IMAGE_SIZE_DISPLAY;
};

const getMaxSize = (type: UploadType) => {
  switch (type) {
    case ActionTypeEnum.photo:
      return {
        format: IMAGE_ACCEPT_FORMAT,
        maxSize: MAX_IMAGE_SIZE,
      };
    case ActionTypeEnum.video:
      return {
        format: VIDEO_ACCEPT_FORMAT,
        maxSize: MAX_VIDEO_SIZE,
      };
    default:
      break;
  }
  return {
    format: IMAGE_ACCEPT_FORMAT,
    maxSize: MAX_IMAGE_SIZE,
  };
};


export const checkFileAsync = (file: File, filePreview: string, type: UploadType, maxSize?: number) => (
  getFileSize(file, maxSize || getMaxSize(type)?.maxSize, getMaxSize(type)?.format).then((res: any) => {
    if (res.error) {
      return res;
    }
    if (type === ActionTypeEnum.video) {
      return checkVideoDuration(filePreview).then(res => {
        if (res.error) {
          return res;
        }
        return checkFileTrueType(file, ActionTypeEnum.video)
      });
    } if (type === ActionTypeEnum.photo) {
      return checkPhotoResolution(filePreview).then(res => {
        if (res.error) {
          return res;
        }
        return checkFileTrueType(file, ActionTypeEnum.photo)
      });
    }
    return null;
  }));

// 图片：上限10000 * 10000 ；个数上限20个；支持长图；
// 视频：本地视频上传，单个上传上限2G，时长不超过30min；个数上限20个；
// 链接：支持链接形式解析，能解析链接按现有白名单配置生效；

export const ErrorMsg = (type: ActionTypeEnum, error: any, customMsgValue?: string) => {
  const errorType: any = {
    MAX_SIZE: 'size',
    IMAGE_ACCEPT_FORMAT: 'format',
    MAX_IMAGE_RESOLUTION: 'resolution',
    MAX_VIDEO_DURATION: 'duration',
  };

  if (type === ActionTypeEnum.video && error === 'IMAGE_ACCEPT_FORMAT') {
    // return '视频文件类型不支持'; 匹配接口返回的错误
    return {
      id: 'error_upload_video_support_tips',
    }
  }

  return {
    id: `error_upload_${type}_${errorType[error]}`,
    value: {
      size: customMsgValue || getFileSizeLimitDisplay(type),
      format: type === ActionTypeEnum.video
        ? VIDEO_ACCEPT_FORMAT_DISPLAY : IMAGE_ACCEPT_FORMAT_DISPLAY,
      duration: MAX_VIDEO_DURATION_DISPLAY,
      width: MAX_IMAGE_WIDTH,
      height: MAX_IMAGE_HEIGHT,
    },
  };
};

const covertBase64toFile = (base64url: string, fileName: string) => {
  const arr = base64url.split(',');
  const mime = (arr[0]?.match(/:(.*?);/) || [])[1];
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
};

export const getVideoCoverUrl = (file: File) => {
  return new Promise((resolve, reject) => {
    let dataURL = '';
    const video = document.createElement('video');
    video.setAttribute('crossOrigin', 'anonymous'); // 处理跨域
    video.setAttribute('src', window.URL.createObjectURL(file));
    video.setAttribute('width', '686');
    video.setAttribute('height', '386');
    video.setAttribute('preload', 'auto');

    video.addEventListener('loadeddata', function () {
      const canvas = document.createElement("canvas"),
            width = video.videoWidth, //canvas的尺寸和图片一样
            height = video.videoHeight;
      canvas.width = width || 686;
      canvas.height = height || 386;
      canvas.getContext('2d')?.drawImage(video, 0, 0, width, height); // 绘制canvas
      dataURL = canvas.toDataURL('image/jpeg'); // 转换为base64
      const coverFile = covertBase64toFile(dataURL, `cover-${file.name.split('.')[0]}`);
      resolve({ dataURL, coverFile });
    });
  });
}

export const getVideoCoverMobile = async (file: File) => {
  const videoElem = document.createElement('video');
  videoElem.src = window.URL.createObjectURL(file);
  videoElem.setAttribute('muted', 'muted');
  videoElem.setAttribute('crossOrigin', 'anonymous');
  videoElem.load();
  videoElem.currentTime = 1;
  return new Promise((resolve) => {
    videoElem.onloadeddata = async () => {
      setTimeout(() => {
        let canvas:any = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 200;
        canvas.getContext('2d')?.drawImage(videoElem, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL('image/jpeg');
        canvas = null;
        const coverFile = covertBase64toFile(dataURL, `cover-${file.name.split('.')[0]}`);
        resolve({ dataURL, coverFile });
      }, 500);
    };
  });
};


export const preCheckFiles = (
  files: File[],
  type: UploadType,
  maxSize?: number,
): Promise<IOnChangeCbParam> => {
  const filesList: IProcessFile[] = Array.from(files).map((file) => ({
    url: URL.createObjectURL(file),
    file,
  }));

  const tasks = Array.from(filesList).map(
    (fileItem: any) => checkFileAsync(fileItem.file, fileItem.url, type, maxSize),
  );

  let acceptFiles: IProcessFile[] = [];
  const rejectFiles: IProcessFile[] = [];

  return new Promise((resolve, reject)=>{
    Promise.all(tasks).then((results) => {
      Array.from(filesList).forEach((file, index) => {
        const {
          error, duration, checked, width, height,
        } = results[index];

        if (checked) {
          acceptFiles.push({ ...file, width, height, duration });
        } else {
          file.error = error;
          rejectFiles.push(file);
        }
      })
      resolve({ acceptFiles, rejectFiles });
    })
  });
}

const fileTypeConfig = {
  photo: {
    ACCEPT_TYPES: IMAGE_ACCEPT_FORMAT,
    error: 'IMAGE_ACCEPT_FORMAT'
  },
  video: {
    ACCEPT_TYPES: VIDEO_ACCEPT_FORMAT,
    error: 'IMAGE_ACCEPT_FORMAT'
  }
}
/**
 * 解析 MIME ，检查文件真实类型是否符合规定
 * @param file 文件
 * @param type 视频 | 图片
 * @returns
 */
export function checkFileTrueType(file: File, type: UploadType = 'photo') {
  const { ACCEPT_TYPES, error } = fileTypeConfig[type as 'photo' | 'video'];
  const acceptTypes = ACCEPT_TYPES.split(',')
  return parseMimeType(file).then((mimeType) => {
    const result = acceptTypes.findIndex((accept) => {
      return mimeType.map(m => m.mime?.toLowerCase()).includes(accept.trim().toLowerCase());
    })
    if (result === -1) {
      return {
        error,
        checked: false,
      }
    }
    return {
      checked: true,
    };
  }).catch(() => {
    return {
      error,
      checked: false,
    }
  })
}

